import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Leaf, Mail, Lock, User, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UserAuth = () => {
  const { getLocalizedText } = useLanguage();
  const { login, register } = useUser();
  const navigate = useNavigate();
  
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');

  const texts = {
    login: { fi: 'Kirjaudu sisään', fr: 'Se connecter', en: 'Sign In' },
    register: { fi: 'Rekisteröidy', fr: "S'inscrire", en: 'Sign Up' },
    email: { fi: 'Sähköposti', fr: 'Email', en: 'Email' },
    password: { fi: 'Salasana', fr: 'Mot de passe', en: 'Password' },
    name: { fi: 'Nimi', fr: 'Nom', en: 'Name' },
    noAccount: { fi: 'Ei tiliä?', fr: 'Pas de compte?', en: "Don't have an account?" },
    hasAccount: { fi: 'Onko sinulla tili?', fr: 'Vous avez un compte?', en: 'Already have an account?' },
    createAccount: { fi: 'Luo tili', fr: 'Créer un compte', en: 'Create account' },
    signIn: { fi: 'Kirjaudu', fr: 'Connectez-vous', en: 'Sign in' },
    welcome: { fi: 'Tervetuloa!', fr: 'Bienvenue!', en: 'Welcome!' },
    joinUs: { fi: 'Liity love4vegan-yhteisöön', fr: 'Rejoignez la communauté love4vegan', en: 'Join the love4vegan community' },
    welcomeBack: { fi: 'Tervetuloa takaisin!', fr: 'Bon retour!', en: 'Welcome back!' },
    signInToContinue: { fi: 'Kirjaudu jatkaaksesi', fr: 'Connectez-vous pour continuer', en: 'Sign in to continue' },
    backHome: { fi: 'Takaisin kotiin', fr: "Retour à l'accueil", en: 'Back to home' },
    forgotPassword: { fi: 'Unohtuiko salasana?', fr: 'Mot de passe oublié?', en: 'Forgot password?' },
    resetPassword: { fi: 'Nollaa salasana', fr: 'Réinitialiser le mot de passe', en: 'Reset Password' },
    sendResetLink: { fi: 'Lähetä nollauslinkki', fr: 'Envoyer le lien', en: 'Send Reset Link' },
    backToLogin: { fi: 'Takaisin kirjautumiseen', fr: 'Retour à la connexion', en: 'Back to login' },
    resetEmailSent: { fi: 'Jos sähköposti löytyy, nollauslinkki on lähetetty!', fr: 'Si l\'email existe, un lien a été envoyé!', en: 'If the email exists, a reset link has been sent!' },
    enterEmail: { fi: 'Syötä sähköpostiosoitteesi', fr: 'Entrez votre email', en: 'Enter your email address' }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(getLocalizedText({ fi: 'Kirjautuminen onnistui!', fr: 'Connexion réussie!', en: 'Logged in successfully!' }));
      } else {
        if (!formData.name.trim()) {
          toast.error(getLocalizedText({ fi: 'Nimi vaaditaan', fr: 'Le nom est requis', en: 'Name is required' }));
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success(getLocalizedText({ fi: 'Tili luotu!', fr: 'Compte créé!', en: 'Account created!' }));
      }
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error(getLocalizedText({ fi: 'Syötä sähköpostiosoite', fr: 'Entrez votre email', en: 'Enter your email' }));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success(getLocalizedText(texts.resetEmailSent));
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      // Still show success message to prevent email enumeration
      toast.success(getLocalizedText(texts.resetEmailSent));
      setShowForgotPassword(false);
      setForgotEmail('');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
        <div className="max-w-md w-full space-y-8">
          <button 
            onClick={() => setShowForgotPassword(false)} 
            className="inline-flex items-center text-foreground/60 hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {getLocalizedText(texts.backToLogin)}
          </button>

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Leaf className="h-10 w-10 text-primary" />
              <span className="text-3xl font-bold text-primary font-serif">love4vegan</span>
            </div>
            <h1 className="text-3xl font-bold">
              {getLocalizedText(texts.resetPassword)}
            </h1>
            <p className="text-foreground/60 mt-2">
              {getLocalizedText(texts.enterEmail)}
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className="mt-8 space-y-6 bg-card p-8 rounded-2xl border border-border">
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {getLocalizedText(texts.email)}
              </Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="h-12"
                data-testid="forgot-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full text-lg"
              data-testid="forgot-submit-btn"
            >
              {loading ? '...' : getLocalizedText(texts.sendResetLink)}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="user-auth-page">
      <div className="max-w-md w-full space-y-8">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center text-foreground/60 hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {getLocalizedText(texts.backHome)}
        </Link>

        {/* Logo & Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Leaf className="h-10 w-10 text-primary" />
            <span className="text-3xl font-bold text-primary font-serif">love4vegan</span>
          </div>
          <h1 className="text-3xl font-bold">
            {getLocalizedText(isLogin ? texts.welcomeBack : texts.welcome)}
          </h1>
          <p className="text-foreground/60 mt-2">
            {getLocalizedText(isLogin ? texts.signInToContinue : texts.joinUs)}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6 bg-card p-8 rounded-2xl border border-border">
          <div className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {getLocalizedText(texts.name)}
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="h-12"
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {getLocalizedText(texts.email)}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {getLocalizedText(texts.password)}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                className="h-12"
                data-testid="password-input"
              />
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {getLocalizedText(texts.forgotPassword)}
                </button>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full text-lg"
            data-testid="auth-submit-btn"
          >
            {loading ? '...' : getLocalizedText(isLogin ? texts.login : texts.register)}
          </Button>

          <div className="text-center">
            <span className="text-foreground/60">
              {getLocalizedText(isLogin ? texts.noAccount : texts.hasAccount)}{' '}
            </span>
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {getLocalizedText(isLogin ? texts.createAccount : texts.signIn)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
